import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Truck, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ACCOUNT } from "@/constants/testIds";

const STATUS_STYLES = {
  paid: "border-primary text-primary",
  pending: "border-border text-muted-foreground",
  cancelled: "border-destructive text-destructive",
};

export const StatusPill = ({ status }) => (
  <span className={`text-[10px] uppercase tracking-label px-2 py-1 border ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {status || "pending"}
  </span>
);

export const FulfillPill = ({ fulfillment }) => {
  if (!fulfillment) return null;
  return (
    <span className="text-[10px] uppercase tracking-label px-2 py-1 border border-secondary text-secondary">
      {fulfillment.status.replace("_", " ")}
    </span>
  );
};

export const EmptyState = ({ icon: Icon, title, cta }) => (
  <div className="editorial-card py-16 text-center">
    <Icon className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
    <p className="mt-4 text-sm uppercase tracking-label text-muted-foreground">{title}</p>
    <div className="mt-4 text-sm">{cta}</div>
  </div>
);

export const OrderRow = ({ order }) => {
  const isPaid = order.payment_status === "paid";
  return (
    <div className="editorial-card p-6 flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-label text-muted-foreground">
          Order · {new Date(order.created_at).toLocaleDateString()}
        </p>
        <p className="mt-1 text-sm">
          {(order.items || []).map((i) => `${i.name} × ${i.qty}`).join(", ") || "—"}
        </p>
        {isPaid && order.fulfillment && (
          <p className="mt-2 text-[11px] uppercase tracking-label text-muted-foreground">
            Tracking · {order.fulfillment.tracking_number} · ETA {order.fulfillment.estimated_delivery}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-serif text-xl">${(order.amount || 0).toFixed(2)}</span>
        <StatusPill status={order.payment_status} />
        {isPaid && <FulfillPill fulfillment={order.fulfillment} />}
        {isPaid && (
          <Link
            to={`/order/${order.session_id}`}
            data-testid={ACCOUNT.trackOrderBtn(order.session_id)}
            className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-label border border-foreground hover:bg-foreground hover:text-background luxe-btn"
          >
            <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> Track
          </Link>
        )}
      </div>
    </div>
  );
};

export const KundliRow = ({ kundli, onDelete }) => (
  <details className="editorial-card p-6 group">
    <summary className="cursor-pointer flex items-center justify-between list-none">
      <div>
        <p className="font-serif text-xl">{kundli.name}</p>
        <p className="text-[11px] uppercase tracking-label text-muted-foreground mt-1">
          {kundli.dob} · {kundli.tob} · {kundli.pob}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground group-open:hidden">Expand</span>
        <button
          data-testid={ACCOUNT.deleteKundli(kundli.id)}
          onClick={(e) => { e.preventDefault(); onDelete(kundli.id); }}
          className="p-2 text-muted-foreground hover:text-primary"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </summary>
    <div className="mt-6 whitespace-pre-wrap text-[15px] leading-relaxed">{kundli.prediction}</div>
  </details>
);

const RescheduleForm = ({ booking, onSubmit, saving }) => {
  const [date, setDate] = React.useState("");
  const [time, setTime] = React.useState("");
  const minDate = new Date(Date.now() + 60 * 60 * 1000).toISOString().split("T")[0];

  const submit = () => onSubmit(booking, { date, time });

  return (
    <div className="mt-5 pt-5 border-t border-border grid sm:grid-cols-3 gap-3 items-end">
      <div>
        <Label className="text-xs uppercase tracking-label">New date</Label>
        <Input type="date" min={minDate} data-testid={ACCOUNT.rescheduleDate} value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 rounded-none border-0 border-b bg-transparent" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-label">New time</Label>
        <Input type="time" data-testid={ACCOUNT.rescheduleTime} value={time} onChange={(e) => setTime(e.target.value)} className="mt-2 rounded-none border-0 border-b bg-transparent" />
      </div>
      <button
        onClick={submit}
        disabled={saving}
        data-testid={ACCOUNT.rescheduleConfirm}
        className="px-4 py-2.5 bg-primary text-primary-foreground text-[11px] uppercase tracking-label font-bold luxe-btn disabled:opacity-50"
      >
        {saving ? "Saving…" : "Confirm new slot"}
      </button>
    </div>
  );
};

export const BookingRow = ({ booking, isRescheduling, onToggleReschedule, onReschedule, onCancel, saving }) => {
  const isCancelled = booking.status === "cancelled";
  const isPaid = booking.payment_status === "paid";
  const isFuture = new Date(booking.slot) > new Date();
  const canActOn = isPaid && !isCancelled && isFuture;

  return (
    <div className={`editorial-card p-6 ${isCancelled ? "opacity-60" : ""}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-label text-muted-foreground">
            Consultation · with {booking.astrologer_name}
          </p>
          <p className="mt-1 font-serif text-lg">{new Date(booking.slot).toLocaleString()}</p>
          {booking.previous_slot && (
            <p className="text-[10px] uppercase tracking-label text-muted-foreground mt-1">
              Rescheduled from {new Date(booking.previous_slot).toLocaleString()}
            </p>
          )}
          {isPaid && !isCancelled && booking.meet_link && (
            <a href={booking.meet_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs uppercase tracking-label text-primary">
              Join video session →
            </a>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-serif text-xl">${(booking.amount || 0).toFixed(2)}</span>
          <StatusPill status={isCancelled ? "cancelled" : booking.payment_status} />
          {canActOn && (
            <>
              <button
                onClick={() => onToggleReschedule(booking.session_id)}
                data-testid={ACCOUNT.rescheduleBtn(booking.session_id)}
                className="px-3 py-2 text-[11px] uppercase tracking-label border border-foreground hover:bg-foreground hover:text-background luxe-btn"
              >
                Reschedule
              </button>
              <button
                onClick={() => onCancel(booking)}
                data-testid={ACCOUNT.cancelBookingBtn(booking.session_id)}
                className="inline-flex items-center gap-1 px-3 py-2 text-[11px] uppercase tracking-label border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground luxe-btn"
              >
                <Ban className="w-3.5 h-3.5" strokeWidth={1.5} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>
      {isRescheduling && !isCancelled && (
        <RescheduleForm booking={booking} onSubmit={onReschedule} saving={saving} />
      )}
    </div>
  );
};
